import Link from 'next/link';
import styled from 'styled-components';
import { useRouter } from 'next/router';

import { FeatureFlags } from 'featureFlags/constants';
import { filterKeysToExclude } from 'search/utils/filterKeysToExclude';
import { SearchFiltersHoverAndSelectedStyle } from 'search/styles/SearchStyles';
import { sendTrackingEvent } from 'tracking/utils/sendTrackingEvent';
import { t } from 'localization/utils/t';
import { useEventContext } from 'tracking/hooks/useEventContext';
import { useFeatureFlag } from 'shared/hooks/useFeatureFlag';
import { variableFontRegular } from 'styles/theme/text';

const TP = 'search.components.searchGrid.SearchClearFilters';

const SearchClearFilters = ({ resultIds }: { resultIds: Array<string> }) => {
  const isConsumerSearchEnabled = useFeatureFlag(FeatureFlags.TEMP_WEB_ENABLE_SEARCH_MICROFRONTEND);
  const { query, pathname, asPath } = useRouter();
  const consumerSearchPathname = asPath?.split('?')[0];
  const facetTypes = Object.keys(query);

  const eventTrackingEventContext = useEventContext();

  const handleClick = () => {
    // reset horizontal scroll back to beginning
    const mobileSortFilter = document.querySelector('[data-qa="mobile_sort_filter_wrapper"]');

    if (mobileSortFilter) {
      mobileSortFilter.scrollLeft = 0;
    }
    sendTrackingEvent('FILTER_RESET_TAP', {
      event_context: eventTrackingEventContext,
      result_id: resultIds[resultIds.length - 1],
    });
  };

  const isCollectionPage = pathname.includes('/collections');
  const hasSubCollection = isCollectionPage && query?.slug?.length > 1;

  // sort and/or search query is applied, no filters and no subcollection on collection page
  if (!hasSubCollection && facetTypes.every((key) => filterKeysToExclude.has(key))) {
    return null;
  }

  // if there is a subcollection, we need to remove it from the query
  const slug = query?.slug?.[0];

  const href = {
    pathname: isConsumerSearchEnabled ? consumerSearchPathname : pathname,
    query: {
      ...(query?.query && { query: query.query }),
      ...(query?.browseKey && { browseKey: query.browseKey }),
      ...(query?.browseValue && { browseValue: query.browseValue }),
      ...(isCollectionPage && { slug }),
    },
  };

  return (
    <Link href={href} passHref scroll shallow legacyBehavior>
      <SearchClearFilters.Text data-qa="clear-filters" onClick={handleClick}>
        {t(`${TP}.reset`, 'Reset')}
      </SearchClearFilters.Text>
    </Link>
  );
};

SearchClearFilters.Text = styled.a`
  margin-left: 12px;
  ${variableFontRegular({ size: '11.5px', height: '14px' })};

  &:hover {
    ${SearchFiltersHoverAndSelectedStyle};
  }

  @media (min-width: ${({ theme }) => theme.breakpoints.mediumSmall}px) {
    margin-left: 0;
  }
`;

export default SearchClearFilters;
